import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useDateStore } from "../stores/date-store";
import { apiGet } from "../lib/api";
import { formatCurrency, formatDateFr, formatNumber, parisToday } from "../lib/format";
import { SyncBanner } from "../components/sync-banner";
import { IbkrPortfolioCard } from "../components/ibkr-portfolio";

export const Route = createFileRoute("/")({
  component: Dashboard,
});

interface Stats {
  totals: {
    totalEntries: number;
    totalDebit: number;
    totalCredit: number;
  };
  byJournal: Array<{
    journalCode: string;
    journalLabel: string;
    count: number;
    debit: number;
    credit: number;
  }>;
  byAccount: Array<{
    accountCode: string;
    accountLabel: string;
    count: number;
    debit: number;
    credit: number;
  }>;
  revenue: number;
  dateRange: { minDate: string | null; maxDate: string | null };
}

interface JobProgress {
  stage: string;
  current: number;
  total: number;
  message: string;
}

interface JobStatus {
  id: string;
  state: "waiting" | "active" | "completed" | "failed" | "delayed" | "stalled";
  progress: JobProgress | number;
  data: { startDate: string; endDate: string; clearExisting: boolean; syncRunId?: number };
  result?: { entriesCreated: number; salesEntries: number; payoutEntries: number; premiumEntries?: number; bankEntries: number; splitEntries?: number; spikoEntries?: number; ibkrEntries?: number; caEntries?: number; openingEntries?: number; loanPaymentMatches?: number; loanAccrualEntries?: number; unresolvedAccountingIssues?: number; warnings?: string[]; errors: string[] };
  failedReason?: string;
  timestamp: number;
  finishedOn?: number;
  processedOn?: number;
}

interface JobsList {
  waiting: Array<{ id: string; data: JobStatus["data"]; timestamp: number }>;
  active: Array<{ id: string; data: JobStatus["data"]; progress: JobProgress | number }>;
  completed: Array<{ id: string; data: JobStatus["data"]; result: JobStatus["result"]; finishedOn: number }>;
  failed: Array<{ id: string; data: JobStatus["data"]; failedReason: string }>;
}

interface AccountingReadiness {
  ready: boolean;
  total: number;
  blocking: number;
  decision: number;
  watch: number;
}

function Dashboard() {
  const { startDate, endDate } = useDateStore();
  const qc = useQueryClient();
  const [feedback, setFeedback] = useState<string | null>(null);

  const { data: stats, isPending, isFetching, refetch } = useQuery<Stats>({
    queryKey: ["stats", startDate, endDate],
    queryFn: () =>
      apiGet<Stats>(
        `/api/stats?${new URLSearchParams({ startDate, endDate })}`,
      ),
  });

  const { data: jobs } = useQuery<JobsList>({
    queryKey: ["jobs"],
    queryFn: () => apiGet<JobsList>("/api/jobs"),
    refetchInterval: 2000,
  });

  const lastCompleted = jobs?.completed?.[0];
  const lastCompletedId = lastCompleted?.id;
  useEffect(() => {
    if (!lastCompletedId) return;
    qc.invalidateQueries({ queryKey: ["stats"] });
    qc.invalidateQueries({ queryKey: ["accounting-issues"] });
    qc.invalidateQueries({ queryKey: ["ibkr-portfolio"] });
  }, [lastCompletedId, qc]);

  // Avant un export, prévient si des problèmes comptables restent ouverts sur la période.
  async function confirmReadiness(period = { startDate, endDate }): Promise<boolean> {
    const readiness = await apiGet<AccountingReadiness>(`/api/accounting-issues/readiness?${new URLSearchParams(period)}`);
    if (readiness.ready) return true;
    return window.confirm(`Cette période n'est pas fiabilisée : ${readiness.total} problème(s) restent à traiter, dont ${readiness.blocking} bloquant(s). Exporter quand même ?`);
  }

  async function handleExportFEC() {
    try {
      if (!(await confirmReadiness())) return;
      window.open(`/api/export/fec?${new URLSearchParams({ startDate, endDate, confirmed: "true" })}`, "_blank");
    } catch (error) {
      setFeedback(`Export impossible : ${(error as Error).message}`);
    }
  }

  async function handleExportPdf() {
    try {
      if (!(await confirmReadiness())) return;
      window.open(`/api/export/reports.pdf?${new URLSearchParams({ startDate, endDate, confirmed: "true" })}`, "_blank");
    } catch (error) {
      setFeedback(`Export impossible : ${(error as Error).message}`);
    }
  }

  // Situation à date : exercice de la date de fin depuis son ouverture, fin
  // ramenée à aujourd'hui (Paris) si elle est dans le futur. La date effective
  // est celle contrôlée ET celle transmise à l'API : même journée des deux côtés.
  async function handleExportSituation() {
    try {
      const today = parisToday();
      const asOf = endDate > today ? today : endDate;
      if (
        asOf !== endDate &&
        !window.confirm(
          `La fin de période (${endDate}) est dans le futur : la situation sera arrêtée à aujourd'hui (${asOf}), sans les écritures datées après. Pour une situation à une autre date, choisissez-la comme fin de période. Continuer ?`,
        )
      ) {
        return;
      }
      if (!(await confirmReadiness({ startDate: `${asOf.slice(0, 4)}-01-01`, endDate: asOf }))) return;
      window.open(`/api/export/situation.pdf?${new URLSearchParams({ endDate: asOf, confirmed: "true" })}`, "_blank");
    } catch (error) {
      setFeedback(`Export impossible : ${(error as Error).message}`);
    }
  }

  if (isPending) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Chargement...</div>
      </div>
    );
  }

  const balance =
    (stats?.totals.totalDebit ?? 0) - (stats?.totals.totalCredit ?? 0);
  const isBalanced = Math.abs(balance) < 0.01;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
      </div>

      <SyncBanner />

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-1">
          Période du {formatDateFr(startDate)} au {formatDateFr(endDate)}
        </h2>
        <p className="text-sm text-gray-500 mb-4">
          La période se change dans la barre en haut de page ; elle s'applique à toutes les pages.
        </p>
        <div className="flex flex-wrap gap-4 items-end">
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2"
          >
            {isFetching && (
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            )}
            Actualiser
          </button>
          {(stats?.totals.totalEntries ?? 0) > 0 && (
            <button
              onClick={handleExportFEC}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium"
            >
              Exporter FEC
            </button>
          )}
          {(stats?.totals.totalEntries ?? 0) > 0 && (
            <button
              onClick={handleExportPdf}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md text-sm font-medium"
              title="Bilan, compte de résultat, soldes intermédiaires de gestion et ratios sur la période"
            >
              Exporter PDF bilan + P&amp;L
            </button>
          )}
          {(stats?.totals.totalEntries ?? 0) > 0 && (
            <button
              onClick={handleExportSituation}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md text-sm font-medium"
              title="Situation comptable à la date de fin de période, depuis l'ouverture de l'exercice : bilan, compte de résultat, balance générale et arrêté à signer (commissaire aux apports, tiers)"
            >
              Exporter situation à date
            </button>
          )}
        </div>
        {feedback && (
          <div className="mt-4 text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded p-3">{feedback}</div>
        )}
      </div>

      <JobsPanel jobs={jobs} />


      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card label="Écritures" value={formatNumber(stats?.totals.totalEntries)} />
        <Card label="Total Débit" value={formatCurrency(stats?.totals.totalDebit)} />
        <Card label="Total Crédit" value={formatCurrency(stats?.totals.totalCredit)} />
        <div
          className={`p-6 rounded-lg shadow-sm border ${
            isBalanced ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"
          }`}
        >
          <div className="text-sm font-medium text-gray-500">Balance</div>
          <div
            className={`mt-2 text-3xl font-bold ${
              isBalanced ? "text-green-700" : "text-red-700"
            }`}
          >
            {isBalanced ? "Équilibré ✓" : formatCurrency(balance)}
          </div>
        </div>
      </div>

      <IbkrPortfolioCard />

      {stats?.dateRange?.minDate && (
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <span className="text-sm text-gray-500">Période: </span>
          <span className="font-medium">{stats.dateRange.minDate}</span>
          <span className="text-gray-400 mx-2">→</span>
          <span className="font-medium">{stats.dateRange.maxDate}</span>
        </div>
      )}

      {stats?.byJournal && stats.byJournal.length > 0 && (
        <Section title="Par Journal">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <Th>Code</Th>
                <Th>Libellé</Th>
                <Th align="right">Écritures</Th>
                <Th align="right">Débit</Th>
                <Th align="right">Crédit</Th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {stats.byJournal.map((j) => (
                <tr key={j.journalCode}>
                  <Td className="font-medium text-gray-900">{j.journalCode}</Td>
                  <Td>{j.journalLabel}</Td>
                  <Td align="right">{formatNumber(j.count)}</Td>
                  <Td align="right">{formatCurrency(j.debit)}</Td>
                  <Td align="right">{formatCurrency(j.credit)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}

      {stats?.byAccount && stats.byAccount.length > 0 && (
        <Section title="Par Compte">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <Th>Compte</Th>
                <Th>Libellé</Th>
                <Th align="right">Débit</Th>
                <Th align="right">Crédit</Th>
                <Th align="right">Solde</Th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {stats.byAccount.map((acc) => {
                const debit = acc.debit ?? 0;
                const credit = acc.credit ?? 0;
                // Sens naturel du compte selon la classe PCG :
                //   1 (capitaux), 7 (produits) → solde créditeur naturel (C - D)
                //   2/3/5/6 → solde débiteur naturel (D - C)
                //   4 → mixte (clients en D, fournisseurs/dettes en C). On traite par sous-compte.
                const isCreditNatural =
                  acc.accountCode.startsWith("1") ||
                  acc.accountCode.startsWith("7") ||
                  acc.accountCode.startsWith("40") ||
                  acc.accountCode.startsWith("42") ||
                  acc.accountCode.startsWith("43") ||
                  acc.accountCode.startsWith("44") ||
                  acc.accountCode.startsWith("419"); // clients - avances reçues
                const solde = isCreditNatural ? credit - debit : debit - credit;
                return (
                  <tr key={acc.accountCode}>
                    <Td className="font-mono font-medium text-gray-900">
                      {acc.accountCode}
                    </Td>
                    <Td>{acc.accountLabel}</Td>
                    <Td align="right">{formatCurrency(debit)}</Td>
                    <Td align="right">{formatCurrency(credit)}</Td>
                    <td
                      className={`px-6 py-4 whitespace-nowrap text-sm text-right font-medium ${
                        solde >= 0 ? "text-green-600" : "text-red-600"
                      }`}
                    >
                      {formatCurrency(solde)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Section>
      )}

      {(!stats?.totals.totalEntries || stats.totals.totalEntries === 0) && (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune écriture</h3>
          <p className="mt-1 text-sm text-gray-500">
            Aucune écriture pour cette période. Utilisez l'onglet Jobs pour générer les
            écritures.
          </p>
        </div>
      )}
    </div>
  );
}

function JobsPanel({ jobs }: { jobs?: JobsList }) {
  if (!jobs) return null;
  const active = jobs.active;
  const waiting = jobs.waiting;
  const lastCompleted = jobs.completed[0];
  const lastFailed = jobs.failed[0];
  const hasActivity = active.length > 0 || waiting.length > 0;

  if (!hasActivity && !lastCompleted && !lastFailed) return null;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-3">
      <h2 className="text-lg font-medium text-gray-900">Jobs</h2>

      {active.map((job) => {
        const progress = typeof job.progress === "object" && job.progress ? job.progress : null;
        const pct = progress && progress.total > 0
          ? Math.min(100, Math.round((progress.current / progress.total) * 100))
          : null;
        return (
          <div key={job.id} className="border border-indigo-200 bg-indigo-50 rounded p-3">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium text-indigo-900">
                Sync {job.data.startDate} → {job.data.endDate}
              </span>
              <span className="text-xs text-indigo-700 font-mono">{job.id}</span>
            </div>
            {progress && (
              <>
                <div className="text-xs text-indigo-700 mt-1">
                  {progress.stage} · {progress.message}
                  {pct !== null && <> · {pct}%</>}
                </div>
                {pct !== null && (
                  <div className="mt-2 w-full bg-white rounded-full h-2 overflow-hidden">
                    <div
                      className="bg-indigo-600 h-2 transition-all"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}

      {waiting.map((job) => (
        <div key={job.id} className="border border-gray-200 bg-gray-50 rounded p-3 text-sm">
          <span className="font-medium text-gray-700">En attente</span>
          <span className="ml-2 text-gray-500">
            Sync {job.data.startDate} → {job.data.endDate}
          </span>
        </div>
      ))}

      {!hasActivity && lastCompleted && lastCompleted.result && (
        <div className="border border-green-200 bg-green-50 rounded p-3 text-sm text-green-900">
          ✓ Dernier sync ({lastCompleted.data.startDate} → {lastCompleted.data.endDate}) :{" "}
          {lastCompleted.result.entriesCreated} écritures (VE : {lastCompleted.result.salesEntries},
          ENC : {lastCompleted.result.payoutEntries}, AC : {lastCompleted.result.premiumEntries ?? 0}, BQ :{" "}
          {lastCompleted.result.bankEntries}
          {lastCompleted.result.splitEntries ? `, dont ${lastCompleted.result.splitEntries} ventilée(s)` : ""}
          {lastCompleted.result.spikoEntries ? `, SP : ${lastCompleted.result.spikoEntries}` : ""}
          {lastCompleted.result.ibkrEntries ? `, IB : ${lastCompleted.result.ibkrEntries}` : ""}
          {lastCompleted.result.caEntries ? `, BC : ${lastCompleted.result.caEntries}` : ""}
          {lastCompleted.result.openingEntries ? `, AN : ${lastCompleted.result.openingEntries}` : ""})
          {lastCompleted.result.unresolvedAccountingIssues ? (
            <a href="/issues" className="ml-1 font-medium text-orange-700 underline">· {lastCompleted.result.unresolvedAccountingIssues} à décider</a>
          ) : null}
          {lastCompleted.result.errors?.length ? (
            <span className="text-orange-700">
              {" "}· {lastCompleted.result.errors.length} erreurs
            </span>
          ) : null}
          {(lastCompleted.result.errors?.length || lastCompleted.result.warnings?.length) ? (
            <details className="mt-1 text-xs">
              <summary className="cursor-pointer text-gray-600">Détail</summary>
              <ul className="mt-1 space-y-0.5 font-mono">
                {lastCompleted.result.errors?.map((e, i) => (
                  <li key={`e${i}`} className="text-orange-800">{e}</li>
                ))}
                {lastCompleted.result.warnings?.map((w, i) => (
                  <li key={`w${i}`} className="text-gray-600">{w}</li>
                ))}
              </ul>
            </details>
          ) : null}
        </div>
      )}

      {!hasActivity && lastFailed && (
        <div className="border border-red-200 bg-red-50 rounded p-3 text-sm text-red-900">
          ✗ Dernier sync échoué ({lastFailed.data.startDate} → {lastFailed.data.endDate}) :{" "}
          <span className="font-mono text-xs">{lastFailed.failedReason}</span>
        </div>
      )}
    </div>
  );
}

function Card({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
      <div className="text-sm font-medium text-gray-500">{label}</div>
      <div className="mt-2 text-3xl font-bold text-gray-900">{value}</div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-medium text-gray-900">{title}</h2>
      </div>
      <div className="overflow-x-auto">{children}</div>
    </div>
  );
}

function Th({ children, align = "left" }: { children: React.ReactNode; align?: "left" | "right" }) {
  return (
    <th
      className={`px-6 py-3 text-${align} text-xs font-medium text-gray-500 uppercase`}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align = "left",
  className = "",
}: {
  children: React.ReactNode;
  align?: "left" | "right";
  className?: string;
}) {
  return (
    <td
      className={`px-6 py-4 whitespace-nowrap text-sm text-${align} ${
        className || "text-gray-500"
      }`}
    >
      {children}
    </td>
  );
}
